import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContinueButtonProps } from '@/formbuilder/formBuilder.constants';
import { Button, ButtonVariant } from '@seeqdev/qomponents';
import type { FormProps } from '@/formbuilder/FormBuilder.page';
import { ButtonWithPopoverAttached } from './ButtonWithPopoverAttached.molecule';

interface CancelSaveAndContinueProps
  extends ContinueButtonProps,
    Pick<FormProps, 'submitOptions' | 'resetToFirstSubmitOptionOnSubmit'> {
  submitFn?: (...values: any[]) => any;
  cancelFn?: () => void;
  values: any[];
  btnDisabled?: boolean;
  cancelBtnLabel?: string;
  submitBtnLabel?: string;
  cancelClassNames?: string;
  submitClassNames?: string;
  cancelBtnTestId?: string;
  submitBtnTestId?: string;
  submitBtnVariant?: ButtonVariant;
  hideSubmit?: boolean;
  hideCancel?: boolean;
  hideContinue?: boolean;
  /** this is useful for those rare cases where the submit button should remain in a loading state after the submitFn is resolved (ex. oauth) */
  continueProcessing?: boolean;
  setIsContinueButtonClicked?: (isContinueButtonClicked: boolean) => void;
}

/**
 * Cancel and Save buttons
 */
export const CancelSaveAndContinue: React.FunctionComponent<CancelSaveAndContinueProps> = (props) => {
  const {
    submitFn,
    cancelFn,
    btnDisabled,
    values,
    cancelBtnLabel = 'CANCEL',
    submitBtnLabel,
    cancelClassNames = '',
    submitClassNames = '',
    cancelBtnTestId = 'cancelButton',
    submitBtnTestId = 'executeButton',
    submitBtnVariant = 'theme',
    hideSubmit,
    hideCancel,
    hideContinue,
    continueProcessing,
    continueButtonLabel,
    continueButtonEnabled,
    continueButtonVariant,
    continueButtonPosition,
    continueButtonExtraClassNames,
    setIsContinueButtonClicked,
    continueButtonDisabled,
    continueButtonOnSubmit,
    submitOptions,
    resetToFirstSubmitOptionOnSubmit,
  } = props;

  const { t } = useTranslation();

  const [processing, setProcessing] = useState(false);
  const [continueButtonProcessing, setIsContinueButtonProcessing] = useState(false);
  const [activeSubmitOptionIndex, setActiveSubmitOptionIndex] = useState(0);

  const isClosed = useRef(false);

  const activeSubmitOption = submitOptions?.[activeSubmitOptionIndex];

  useEffect(
    () => () => {
      isClosed.current = true;
    },
    [],
  );

  const resetButtons = () => {
    setIsContinueButtonProcessing(false);
    setProcessing(false);
  };

  const manageSubmission = async (isExtraButton = false) => {
    isExtraButton ? setIsContinueButtonProcessing(true) : setProcessing(true);
    setIsContinueButtonClicked && setIsContinueButtonClicked(isExtraButton);
    if (resetToFirstSubmitOptionOnSubmit) {
      setActiveSubmitOptionIndex(0);
    }

    const functionToExecute =
      isExtraButton && continueButtonOnSubmit ? continueButtonOnSubmit : activeSubmitOption?.submitFn ?? submitFn;

    return Promise.resolve()
      .then(() => functionToExecute?.(values))
      .finally(() => !isClosed.current && resetButtons());
  };

  const disableContinueButton = continueButtonDisabled ?? btnDisabled;

  const continueSubmitButton = (
    <Button
      id="extraSave"
      type="submit"
      onClick={() => manageSubmission(true)}
      label={continueButtonLabel && t(continueButtonLabel)}
      disabled={
        !continueButtonEnabled || continueButtonProcessing || processing || continueProcessing || disableContinueButton
      }
      size="sm"
      icon={continueButtonProcessing || continueProcessing ? 'fa-spinner fa-spin-pulse fa-lg sq-icon-white mr5' : ''}
      iconPrefix="fa-solid"
      testId="extraButtonTestId"
      extraClassNames={continueButtonExtraClassNames || 'min-width-100'}
      variant={continueButtonVariant}
    />
  );

  return (
    <>
      {!hideCancel && (
        <Button
          onClick={() => cancelFn?.()}
          label={t(cancelBtnLabel)}
          size="sm"
          testId={cancelBtnTestId}
          extraClassNames={cancelClassNames || 'mr20 min-width-100 width-100 max-width-100'}
        />
      )}
      {!hideContinue && continueButtonLabel && continueButtonPosition === 'left' && continueSubmitButton}
      {!hideSubmit &&
        (submitOptions && activeSubmitOption ? (
          <ButtonWithPopoverAttached
            id={activeSubmitOption.key}
            label={activeSubmitOption.label}
            disabled={btnDisabled}
            testId={activeSubmitOption.key}
            onClick={() => manageSubmission(false)}
            variant={activeSubmitOption?.variant ?? submitBtnVariant}
            iconPrefix="fa-solid"
            showPopover={submitOptions.length > 0}
            popoverContent={
              <div className="flexRowContainer ">
                {submitOptions.map((option, index) => (
                  <div
                    key={option.label}
                    onClick={() => {
                      setActiveSubmitOptionIndex(index);
                      option.onSelect?.();
                    }}
                    className="cursorPointer pl20 pr20 ptb4 width-maximum fs14 border-0  textAlignLeft noActiveBg">
                    {t(option.label)}
                  </div>
                ))}
              </div>
            }
          />
        ) : (
          <Button
            id="save"
            type="submit"
            onClick={() => manageSubmission(false)}
            label={submitBtnLabel ? t(submitBtnLabel) : t('SAVE')}
            disabled={btnDisabled || processing || continueProcessing}
            size="sm"
            icon={processing || continueProcessing ? 'fa-spinner fa-spin-pulse fa-lg sq-icon-white mr5' : ''}
            iconPrefix="fa-solid"
            testId={submitBtnTestId}
            extraClassNames={submitClassNames || 'min-width-100'}
            variant={submitBtnVariant}
          />
        ))}
      {continueButtonLabel && continueButtonPosition === 'right' && continueSubmitButton}
    </>
  );
};
